export const MS_DAY = 1000*60*60*24;
export const MS_MIN = 1000*60;
export const secUnits = {second: 1, minute: 60, hour: 60*60, day: 60*60*24, week: 60*60*24*7, month: 60*60*24*30.5, year: 60*60*24*365};
export const secUnitsBiz = {second: 1, minute: 60, hour: 60*60, day: 60*60*8, week: 60*60*8*5, year: 60*60*8*5*52};
export const minUnitsBiz = {second: 1/60, minute: 1, hour: 60, day: 60*8, week: 60*8*5, month: 60*8*5*4.33, year: 60*8*5*52};
export type SecUnitKey = keyof typeof secUnits;
export const units = Object.keys(secUnits) as SecUnitKey[], unitVals: number[] = Object.values(secUnits);
const unitsFirst = units.map(u => u === 'minute'? 'min' : u === 'month'? 'mo' : u[0]);

declare global {
	interface Number {
		plural: string;
		between: number;
	}
	interface String {
		capitalize(): string;
		capitalizeFirst(): string;
		toKebabCase(): string;
		fromKebabCase(): string;
		splitCamel(): string;
		toSnakeCase(): string;
		uncamel(): string;
		plural(num?: number): string;
		format(...args: string[]): string;
		formatRegex(groups: string[], named?: Record<string, string>): string;
		before(sep: string): string;
		beforeLast(sep: string): string;
		after(sep: string): string;
		afterLast(sep: string): string;
		hash(): number;
	}
	interface Array<T> {
		last: T;
		beforeLast: T;
		removeFirst(val: T, ...toVals: T[]): T[];
		removeFirstPred(pred: (item: T) => boolean, ...toVals: T[]): T[];
		removeAny(val: T): T[];
		removeIndexes(idxArr: number[]): T[];
		orderBy(sortSpec: (a: T, b: T) => number): T[];
		findPred(pred: (item: T) => boolean): T | undefined;
		findFirst(comparator?: (a: T, b: T) => number): T;
		findInsertIndex(value: T, comparator?: (a: T, b: T) => number): number;
		insertSorted(value: T, comparator?: (a: T, b: T) => number): T[];
		toggleItem(item: T): T[];
		interleave(item: T): T[];
		groupBy(getGroup: (item: T) => unknown): Record<string, T[]>;
		undup(): T[];
		equals(arr2: T[]): boolean;
		threadAt(value: T, idx: number): T[];
		spliceArr(start: number, deleteCount: number, ...newItems: T[]): T[];
		move(from: number, to: number): T[];
		toObj<R extends object>(cb: (acc: R, item: T, i?: number) => R | void, init?: object): R;
	}
	interface Set<T> {
		toggle(item: T, to?: boolean): Set<T>;
	}
}

if (!('removeFirst' in Array.prototype)) {
	Object.defineProperties(Number.prototype, {
		plural: {get() { return this === 1 ? '' : 's';}},                            // returns plural 's' if number is not 1
		between: {value(r1, r2) { return this >= r1 && this < r2;}},
	});
	Object.defineProperties(Array.prototype, {
		last: {get() { return this[this.length - 1];}},                              // returns last item in array
		beforeLast: {get() { return this[this.length - 2];}},                        // returns before last item in array
		removeFirst: {                                                              // remove first val item. replace with toVals if given. returns removed item.
			value(val, ...toVals) {
				const idx = this.indexOf(val);
				if (idx !== -1)
					return this.splice(idx, 1, ...toVals)[0];
			}
		},
		removeFirstPred: {                                                          // remove first item answering predicate. replace with toVals if given. returns removed item.
			value(pred, ...toVals) {
				const idx = this.findIndex(pred);
				if (idx !== -1)
					return this.splice(idx, 1, ...toVals)[0];
			}
		},
		removeAny: {                                                                // remove all val items from array.
			value(val) {
				for (let i = 0; (i = this.indexOf(val, i)) >= 0; this.splice(i, 1));
				return this;
			}
		},
		removeIndexes: {                                                            // removes idxArr indexes from array. use slice() if you want a copy.
			value(idxArr) {
				idxArr.slice().sort().reverse().forEach(i => this.splice(i, 1));
				return this;
			}
		},
		orderBy: {                                                                  // sort with a sortSpec as: [{f: string, comparator(a,b)}, ...]
			value(sortSpec) {
				return this.sort((oa, ob) => {
					for (let {tmp, f, comparator} of sortSpec)
						if ((tmp = comparator(oa, ob, f))) return tmp;
					return 0;
				});
			}
		},
		findPred: {                                                                 // return the first non-falsy result of pred()
			value(pred) {
				let val;
				this.some((v, i, a) => val = pred(v, i, a));
				return val;
			}
		},
		toggleItem: {                                                               // add / remove item from array
			value(item) {
				const rmv = this.filter(itm => itm !== item);
				return rmv.length !== this.length? rmv : rmv.concat(item);
			}
		},
		findFirst: {
			value(comparator = comparators.generic) {
				return this.slice(1).reduce((a, b) => +comparator(a, b) > 0? b : a, this[0]);
			}
		},
		findInsertIndex: {
			value(value, comparator = comparators.generic) {
				return this.findIndex(v => +comparator(value, v) <= 0);
			}
		},
		insertSorted: {
			value(value, comparator) {
				const idx = this.findInsertIndex(value, comparator);
				this.splice(idx >= 0? idx : this.length, 0, value);
				return this;
			}
		},
		interleave: {                                                               // interleave a new item between each of the existing items in array. like join(item) for arrays returns a new copy.
			value(item) {
				if (this.length < 2) return this.slice();
				const laced = this.slice(0, this.length-1).map(e => [e, item]);
				return [].concat(...laced, this[this.length-1]);
			}
		},
		groupBy: {
			value(getGroup) {                                                     // goes over an array and groups by the key returned from getGroup(ent).
				return this.reduce((acc, ent) => {                                // @returns a dictionary of: {[groupBy]: [values...]}
					const group = getGroup(ent);
					Array.isArray(group) ?
						group.forEach(g => (acc[g] ?? (acc[g] = [])).push(ent))
						: (acc[group] ?? (acc[group] = [])).push(ent);
					return acc;
				}, {});
			}
		},
		undup: {
			value() {
				return Array.from(new Set(this as unknown[]));
			}
		},
		equals: {
			value(arr2) {
				return this === arr2 ||
                    (this.length === arr2?.length && this.every((v, i) => v === arr2[i]));
			}
		},
		threadAt: {
			value(value, idx) {
				return [...this.slice(0, idx), value, ...this.slice(idx, this.length)];
			}
		},
		spliceArr: {
			value(start, deleteCount, ...newItems) {
				this.splice(start, deleteCount, ...newItems);
				return this;
			}
		},
		move: {
			value(from, to) {
				return this.spliceArr(to, 0, this.splice(from, 1)[0]);
			}
		},
		toObj: {                // Call cb with all items in array, passing initial or empty object. Returns the object.
			value<R>(cb: (acc: R, item: any, index: number) => void, init?: object): R {
				return this.reduce((a, b, i) => {
					cb(a, b, i);
					return a;
				}, init ?? {} as R);
			}
		},
	});

	Object.defineProperties(String.prototype, {
		capitalize: {value() { return this.replace(/(^\w|\s\w)(\S*)/g, (_, m1, m2) => m1.toUpperCase()+m2.toLowerCase());}},   // capitalize all words
		capitalizeFirst: {value() { return this[0].toUpperCase()+this.slice(1);}},                                          // capitalize first word only
		toKebabCase: {value() {return this.split(' ').join('_');}},
		fromKebabCase: {value() {return this.split('_').join(' ');}},
		splitCamel: {value() {return this.replace(/([a-z0-9])([A-Z])/g, '$1 $2');}},
		toSnakeCase: {value() { return this.replace(/([a-z])([A-Z])/g, '$1_$2').replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_]/g, '').toLowerCase();}},       // convert "Some Header" & camelCase string into snake_case
		uncamel: {value() { return this.replace(/[A-Z]/g, m => ' '+m.toLowerCase()).trim();}},                               // convert camelCase string into "camel case"
		plural: {value: function (num: number = 2) { return this + (num === 1? '' : this[this.length-1] !== 's'? 's' : 'es');}},
		format: {                                                                   // "My name is $2. I live in $1".format("MZ", "Blah")
			value: function (...args: string[]) {
				return this.replace(/\$(\d+)/g, (_, match) => args[match-1]);
			}
		},
		formatRegex: {                                                             // match = string.match(/regex/); "$1 or $<namedGroup>".format(match.slice(1), match.groups)
			value: function (groups: string[], named?: Record<string, string>) {
				return this.replace(/\$(\d+|<\w+>)/g, (_, grp) => groups[grp] ?? named?.[grp.slice(1, grp.length-1)] ?? '');
			}
		},
		before: {                                                                   // Return the string before first occurrence of given string
			value: function (sep) {                                                 // If not found, returns all string
				const p = this.indexOf(sep);
				return p < 0? this.toString() : this.substring(0, p);
			}
		},
		beforeLast: {                                                               // Return the string before last occurrence of given string
			value: function (sep) {                                                 // If not found, returns all string
				const p = this.lastIndexOf(sep);
				return p < 0? this.toString() : this.substring(0, p);
			}
		},
		after: {                                                                    // Return string after first occurrence of given string
			value: function (sep) {                                                 // If not found, returns ""
				const p = this.indexOf(sep);
				return p < 0 ? '' : this.substring(p + sep.length);
			}
		},
		afterLast: {                                                                // Return string after last occurrence of given string
			value: function (sep) {                                                 // If not found, returns ""
				const p = this.lastIndexOf(sep);
				return p < 0 ? '' : this.substring(p + sep.length);
			}
		},
		hash: {                                                                // Return string after last occurrence of given string
			value: function () {                                                 // If not found, returns ""
				let hash = 9;
				for (let i = 0; i < this.length; i++)
					hash = Math.imul(hash ^ this.charCodeAt(i), 9 ** 9);
				return hash ^ hash >>> 9;
			}
		},
	});

	Object.defineProperties(Set.prototype, {
		toggle: {value(val, to) { return (to === true || (!this.delete(val) && to !== false)) && this.add(val);}},
	});
}

interface DurOptions {
	isSec?: boolean;
	sep?: string;
	addUnit?: 1 | 2;
	topOnly?: number;
	trimUnits?: boolean;
	trimLeftZero?: boolean | 1;
	showMs?: boolean | 1;
	altUnits?: Record<string, number>;
}
/**
 * Converts number of seconds or milliseconds to sexagesimal or other viewable string formats.
 *  @param msSec
 *  @param isSec
 *  @param sep
 *  @param addUnit (number)?: 1: add 1 character for unit (e.g: 1h:22m), 2: add full unit name (e.g.: 1 hour: 22 minutes)
 *  @param topOnly (number)?: only return most significant (number) of units.
 *  @param trimUnits remove all 0 units, except the last 1.
 *  @param trimLeftZero trims left most zero if 2 digit number or decimals if 0.
 *  @param showMs
 *  @param altUnits
 * */
export const get_dur = (msSec: number, {
	isSec, sep = ':', addUnit, topOnly, trimUnits, trimLeftZero, showMs, altUnits
}: DurOptions = {}): string => {
	const uv = altUnits ? Object.values(altUnits) : unitVals;
	const ut = altUnits ? Object.keys(altUnits) : units;
	const utf = !altUnits? unitsFirst : ut.map(u => u[0]);
	const secs = isSec ? msSec : msSec / 1000;
	let parts: string[] = [];

	for (let i = 0; !i || secs >= uv[i] - 1; i++) {
		const dv = secs % (uv[i + 1] || secs + 1) / uv[i];
		const str =
			!i && showMs && (!trimUnits || secs < 10)
				? ((dv * 1000) | 0) / 1000
				: !addUnit
					? String(dv | 0).padStart(2, '0')
					: String(dv | 0);

		parts.unshift(str + (addUnit === 1 ? utf[i] : addUnit === 2 ? ` ${ut[i] + (dv | 0).plural}` : ''));
	}

	if (topOnly) parts = parts.slice(0, topOnly);

	if (trimUnits) {
		const last = parts[parts.length - 1];
		parts = parts.filter(p => !/^00?(\.000)?[a-z]*$/.test(p));
		if (!parts.length) parts.push(last);
	}

	if (trimLeftZero) parts[0] = parts[0].replace(/^0(?=\d[a-z]?)|\.000/, '');

	return parts.join(sep);
};

export function durPassed(date: string | Date, opts?) {
	if (!date) return;
	const ms: number = Date.now() - new Date(date).getTime();
	return get_dur(ms > 0 ? ms : 0, {trimLeftZero: 1, addUnit: 1, topOnly: 1, trimUnits: 1, ...opts});
}

export const getAgo = (date: string | Date, opts?) => date && (durPassed(date, opts) + ' ago');

export const get_Hmm = (dt) => {
	if (typeof dt === 'number') dt = new Date(dt);
	return dt.getHours() +':'+('0'+dt.getMinutes()).substr(-2);
};

export const get_ddMM = (dt) => {
	if (typeof dt === 'number') dt = new Date(dt);
	return dt.getDate() +'/'+(dt.getMonth()+1);
};

export const get_ddMM_Hmm = (dt, noDate) => {
	if (typeof dt === 'number') dt = new Date(dt);
	return (!noDate? get_ddMM(dt)+', ' : '') + get_Hmm(dt);
};

export const getDateTech = () =>                // moment().format("YYYY-MM-DD");
	new Date(Date.now() - new Date().getTimezoneOffset() * 60 * 1000).toISOString().split('T')[0];

const monthAbbrs = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
// "2026-Jun" or "2026-06" -> "June 2026"; "" for unparseable input.
export const monthLabel = (ym?: string) => {
	if (!ym) return '';
	const [y, mRaw] = ym.split('-');
	const year = Number(y);
	const month = /^\d+$/.test(mRaw) ? Number(mRaw) - 1 : monthAbbrs.indexOf(mRaw?.toLowerCase());
	if (!Number.isFinite(year) || month < 0 || month > 11) return '';
	return new Date(year, month, 1).toLocaleString('en-US', {month: 'long', year: 'numeric'});
};

export const modDate = (d: string, fn: (n: Date) => void): Date => {const n = d ? new Date(d) : new Date(); fn(n); return n;};

export function convertToCSV(jsonData, keys) {
	let headers = Object.keys(jsonData[0]);
	if (keys) headers = headers.filter(h => keys.includes(h));
	return headers +'\n' + jsonData.map(obj => headers.map(h =>
		Array.isArray(obj[h]) ? `"${obj[h].join(',')}"` : obj[h])).join('\n');
}

export const msTZ = new Date().getTimezoneOffset() *60* -1000;
export const isoToMs = iso => new Date(iso).getTime() + msTZ + MS_DAY;
export const msToInputDate = ms => ms*ms > 0? new Date(ms).toISOString().slice(0, 10) : null;
export const msToDays = ms => {
	ms = new Date(ms).getTime() - Date.now();
	return ms*ms > 0? Math.ceil(ms / MS_DAY) : null;
};
export const utcMsToDate = ms => new Date(ms + msTZ).toISOString().slice(0, 10);
export const dateToUtcMs = ds => new Date(ds).getTime() ;

// Returns name of caller function, or given idx, index of previous caller in the call stack.
export const funcName = (idx, opts) => getCallStack(idx ?? 2, opts);      // new Error().stack.match(/(?<=at )\w+/g)[idx ?? 1];

// Returns given idx, name of caller index in the call stack, otherwise returns an array of all function names in the call stack.
export const getCallStack = (idx?: number, opts: {nonNull?: boolean} = {}): string | string[] => {
	const orig = Error.prepareStackTrace;
	Error.prepareStackTrace = (_, stack) => stack;
	const {stack}: any = new Error();
	Error.prepareStackTrace = orig;
	return opts.nonNull?
		stack?.slice(idx).findPred(v => v.getFunctionName()) :
		idx? stack?.[idx]?.getFunctionName() :
			stack?.map(s => s.getFunctionName());
};

export async function timed(func) {
	const start = performance.now();
	await func();
	console.log(`Function ${func.name} took ${(performance.now() - start) | 0}ms`);
}

export const isInteger = (num: unknown): boolean => /^-?[0-9]+$/.test(num+'');
export const getPct = (v, m, decimals = 1) => ((v / m * (10 ** (2+decimals)) |0) / (10 ** decimals)).toLocaleString() + '%';

export const commafy = n => (n && !isNaN(n) || n === 0) && (n * 1).toLocaleString();
export const commafyPlural = (n, text, addSinglePlural?) => n && !isNaN(n) || n === 0?       // addSinglePlural?: array of 2 strings: [0] to add in singular, [1] in plural.
	`${(n*1).toLocaleString()}${!text? '' : ` ${text}${(+n).plural}`}${!addSinglePlural? '' : ' '+ addSinglePlural[(+n !== 1)? 1 : 0]}` : undefined;

export const isSearchInStrings = (search, ...strings) =>
	search.toLowerCase().split(' ').every(srch => strings.flat().some(str => str && str.toLowerCase().includes(srch)));

export const pickKeys = <T extends object, K extends keyof T>(obj: T, ...keys: (K | K[])[]): Pick<T, K> =>
	(keys.flat() as K[]).reduce((a, b) => ((obj[b] !== undefined || obj.hasOwnProperty(b)) && (a[b] = obj[b]), a), {} as Pick<T, K>);
export const sortKeys = <T extends object>(obj: T) => pickKeys(obj, ...Object.keys(obj).sort() as (keyof T)[]);

export const isObject = (v) => typeof v === 'object' && v !== null && !Array.isArray(v);

// Creates an object with keys from array or object, setting the values with the result of pred(key,value)
export function objMap(obj, pred) {
	if (Array.isArray(obj))
		return obj.reduce((a, k, i) => ((a[k] = pred(k, i)), a), {});
	else
		return Object.entries(obj).reduce((a, [k, v], i) => ((a[k] = pred(k, v, i)), a), {});
}

// Create an object holding only keys with value of pred(key,value,i) and only if not undefined.
//
export const objPred = (obj, pred) => {
	let vp;
	if (Array.isArray(obj))
		return obj.toObj((a, k, i) => (vp = pred(k, i)) !== undefined && (a[k] = vp));
	else
		return Object.entries(obj).toObj((a, [k, v], i) => (vp = pred(k, v, i)) !== undefined && (a[k] = vp));
};

// Create an object holding only keys where pred(key,value,i) is true
export const objFilter = <T extends object>(obj: T, pred: (key: keyof T, value: T[keyof T], index: number) => boolean): Partial<T> =>
	Object.entries(obj).toObj((a: Partial<T>, [k, v], i) => pred(k as keyof T, v as T[keyof T], i) && (a[k as keyof T] = v as T[keyof T], a));

// Remove some/all key/values from an object.
export const objRmKeys = (obj, ...keys) => (keys.flat().forEach(k => delete obj[k]), obj);

export const objRmKeysC = (obj, ...keys) => objRmKeys({...obj}, ...keys);
// Get object/array nested path within object/array, or ensure to create it if missing.
// Each item in path can be a string key or a numerical index within array or a tuple array with [key/index, init value].
// init value can be a function generating an init value.
export const objPathInit = (obj, ...path) =>
	path.reduce((a, b) => {
		const [k, v = {}] = Array.isArray(b)? b : [b];
		return a[k] || (a[k] = funcOrValOr(v));
	}, obj);

/**
 * Given an Object and a list of sorter keys, returns a sorted array of [key, value]. Missing keys in either arrays will be dropped.
 *  @param obj object to sort.
 *  @param sorterArr array of keys corresponding to object keys, in arbitrary sort order.
 * */
export function sortObjToTupleByAnotherArr(obj, sorterArr) {
	return sorterArr.filter(k => obj[k]).map(k => obj[k] && ([k, obj[k]])).filter(v => v);
}

export const sortArrByAnotherArr = (arr, sorterArr, pred?) =>
	arr?.filter(v => sorterArr.includes(pred?.(v) ?? v)).sort((a, b) =>
		sorterArr?.indexOf(pred?.(a) ?? a) - sorterArr?.indexOf(pred?.(b) ?? b));

/**
 * Returns sorted array of objects according to another array. Missing keys in either arrays will be dropped.
 *  @param objArr array of objects to sort.
 *  @param key key within objArr objects that will be used to match vs sorterObj
 *  @param sorterArr array of keys corresponding to key keys, in arbitrary sort order.
 * */
export function sortObjArrByAnotherArr(objArr, key, sorterArr) {
	const toObj = objArr.toObj((a, o) => a[o[key]] = o);
	return sorterArr.map(k => toObj[k]).filter(Boolean);
}

// Convert object to array with the keys inserted into each item as k.
export const objToArrK = <T extends Record<string, unknown>>(obj: T) =>
	(Object.entries(obj) as [keyof T, T[keyof T]][]).map(([k, v]) =>
		({k, ...(v && (typeof v === 'object' || typeof v === 'function') ? funcOrValOr(v) : {[k]: v})})
	) as {
        // build one row per key, then union-flatten and wrap in an array
		[P in keyof T]:
        {k: P} &
        (T[P] extends object ? T[P] : Record<P, T[P]>)
	}[keyof T][];

export const isStringRTL = s => /[\u0590-\u07FF\u200F\u202B\u202E\uFB1D-\uFDFD\uFE70-\uFEFC]/.test(s);

export const makeArr = n => [...new Array(n).keys()];
export const isInRange = (v, min, max) => v >= min && v <= max;
export const minMax = (v, min, max) => Math.max(min, Math.min(max, v));


export const funcOrValOr = <T, P>(obj: ((params?: P) => T) | T, params?: P, or?: T): T | undefined =>
	(obj instanceof Function ? obj(params) : obj !== undefined? obj : or);

export type Comparator<T, ExtraArgs extends readonly unknown[] = []> = (
	a: T,
	b: T,
	...args: ExtraArgs
) => number;

export const comparators: {
	generic:            Comparator<any>;
	number:             Comparator<number>;
	numberRev:          Comparator<number>;
	numberSig:          Comparator<number | undefined>;
	numberSigRev:       Comparator<number | undefined>;
	string:             Comparator<string>;
	stringRev:          Comparator<string>;
	stringSig:          Comparator<string | undefined>;
	stringSigRev:       Comparator<string | undefined>;
	dateSig:            Comparator<Date | string | undefined>;
	dateSigRev:         Comparator<Date | string | undefined>;
	numberArrFirst:     Comparator<number[] | undefined>;
	numberArrFirstRev:  Comparator<number[] | undefined>;
	stringArrFirst:     Comparator<string[] | undefined>;
	stringArrFirstRev:  Comparator<string[] | undefined>;
	stringArr1st2nd:    Comparator<string[] | undefined>;
} = {
	generic:            (a, b) => a < b? -1 : +(a > b),
	number:             (a, b) => a - b,                 // suitable for number, boolean, Date, or numerical strings.
	numberRev:          (a, b) => b - a,
	numberSig:          (a, b) => a === b? 0 : a === undefined? 1 : b === undefined? -1 : a - b,   // Significant: undefineds go last
	numberSigRev:       (a, b) => a === b? 0 : a === undefined? 1 : b === undefined? -1 : b - a,
	string:             (a, b) => a.localeCompare(b),
	stringRev:          (a, b) => b.localeCompare(a),
	stringSig:          (a, b) => a === b? 0 : !a? 1 : !b? -1 : a.localeCompare(b),
	stringSigRev:       (a, b) => a === b? 0 : !a? 1 : !b? -1 : b.localeCompare(a),
	dateSig:            (a, b) => a === b ? 0 : !a ? 1 : !b ? -1 : new Date(a).getTime() - new Date(b).getTime(),
	dateSigRev:         (b, a) => a === b ? 0 : !a ? 1 : !b ? -1 : new Date(a).getTime() - new Date(b).getTime(),
	numberArrFirst:     (a, b) => a === b? 0 : a === undefined? 1 : b === undefined? -1 : a[0] - b[0],
	numberArrFirstRev:  (a, b) => a === b? 0 : a === undefined? 1 : b === undefined? -1 : b[0] - a[0],
	stringArrFirst:     (a, b) => a === b? 0 : !a? 1 : !b? -1 : a[0].localeCompare(b[0]),
	stringArrFirstRev:  (a, b) => a === b? 0 : !a? 1 : !b? -1 : b[0].localeCompare(a[0]),
	stringArr1st2nd:    (a, b) => (a[0] ?? '').localeCompare(b[0] ?? '') || (a[1] ?? '').localeCompare(b[1] ?? ''),
};

export const comparatorRev = <T>(comp: Comparator<T>): Comparator<T> => (a, b) => comp(b, a);

/**
 * Creates a comparator that compares two objects by the value of a given field.
 *
 * @param comp   The field-level comparator (e.g. `comparators.number`).
 * @param field  The key to pull from each object.
 * @param isRev  If `true`, reverse the result.
 */
export const comparatorsField = <
	Obj extends Record<string, any>,            // the object being sorted
	FieldKey extends keyof Obj,                 // key inside the object
	FieldVal = Obj[FieldKey]                    // value at that key
>(
	comp: Comparator<FieldVal, [Obj, Obj]>,
	field: FieldKey,
	isRev: boolean = false
): Comparator<Obj> =>
	!isRev
		? (a, b) => comp(a?.[field], b?.[field], a, b)
		: (b, a) => comp(a?.[field], b?.[field], b, a);


export const comparatorsPred = <
	Src,                         // original element type
	Proj                         // projected value type
>(
	comp: Comparator<Proj>,
	pred: (v: Src) => Proj,
	isRev: boolean = false
): Comparator<Src> => !isRev?
	((a, b) => comp(pred(a), pred(b))) :
	((b, a) => comp(pred(a), pred(b)));


// Until test(inp) returns truish, repeat calling inp = next(inp). Returns inp passing test.
export function find(init, test, next) {
	for (; !test(init); init = next(init));
	return init;
}

// keep generating random until number is not in notArr.
export const randomNot = (limit, notArr) => limit > notArr.length && find(-1, v => v >= 0 && !notArr.includes(v), () => Math.random() * limit |0);

export const delay = ms => new Promise(resolve => setTimeout(resolve, ms));

export function unCatch(func, ...params) {
	try {
		return func(...params);
	} catch (e) {}
}

// check return instanceof Error
export function catchErr(func, ...params) {
	try {
		return func(...params);
	} catch (e) {
		return e;
	}
}


export type Callback<T> = (val: T) => void;

export class CallbackRegistry<T> {
	private curVal: T = undefined;
	private fetcher?: (setter: Callback<T>) => void;
	private inFetch = false;
	private subs: Callback<T>[] = [];

	constructor(fetcher?: typeof this.fetcher, initVal?: T) {
		if (initVal !== undefined) this.curVal = initVal;
		this.setFetcher(fetcher);
	}

	setFetcher = (fetcher?: typeof this.fetcher) => {
		this.fetcher = fetcher;
		fetcher && this.refetch();
	};

	subscribe = (cb: Callback<T>) => {
		this.subs.push(cb);
		if (this.curVal !== undefined)
			cb(this.curVal);
		return () => this.unsubscribe(cb);
	};

	setVal = (val: T) => {
		this.curVal = val;
		this.subs.forEach(sub => sub(val));
		return val;
	};

	refetch = () => {
		if (this.inFetch || !this.fetcher) return;       // prevent parallel fetching.
		this.inFetch = true;
		this.fetcher(val => {
			this.inFetch = false;
			this.setVal(val);
		});
	};
	unsubscribe = (cb: Callback<T>): void => void this.subs.removeFirst(cb);
	touch = () => this.setVal(this.curVal);
	getCur = (): T => this.curVal;
}

const SI_SYMBOL = 'kMGTPE';                                     // International System of Units

/**
 * Abbreviates number to up to 3 digits with size symbol.
 *  @param num number to abbreviate.
 * */
export function abvNum(num) {
	const digits = Math.log10(Math.abs(num)) |0;            // what tier? (determines SI symbol)
	const sig = digits % 3;                                 // how many significant digits to show, after the first
	return (Math.round(num / Math.pow(10, digits - sig - +!sig)) / (sig? 1 : 10)) +    // if only first, show 1 decimal
        (SI_SYMBOL[Math.floor(digits / 3 - 1)] ?? '');      // format number and add suffix
}

/**
 * Abbreviates number to up to 3 digits with size symbol.
 *  @param num number to abbreviate.
 *  @param all (boolean)?: return number relative to all smaller abbreviation symbols.
 *  @param minAbv (number)?: if num below minAbv, returns num just commafied.
 *  @param div (number)?: divisor. default: 1000.
 * */
export function abvNumEx(num, {all, minAbv, div}: {all?:boolean, minAbv?:number, div?: number} = {}) {
	div = div ?? 1000;
	if (Math.abs(num) < (minAbv ?? div)) return num.toLocaleString();
	let abv = '';
	for (let tier = Math.log(num) / Math.log(div) |0, i = 0; tier && (!i || all); tier--, i++) {  // what tier? (determines SI symbol)
		let an = num / Math.pow(div, tier);
		an = an < 10 ? (an * 10 +0.5 | 0) / 10 : an +0.5 | 0;                                     // if single digit (no exponents), show fraction
		abv+= (!abv? '' : '  ')+ (!i? an : an.toLocaleString()) + SI_SYMBOL[tier - 1];   // format number and add suffix
	}
	return !abv? num.toString() : !all? abv : abv +'  '+ num.toLocaleString();           // if zero, we don't need a suffix
}


// A simple but high quality 53-bit hash. It's quite fast, provides very good* hash distribution, and because it outputs 53 bits,
// has significantly lower collision rates compared to any 32-bit hash
export function cyrb53(str, seed = 0) {
	let h1 = 0xdeadbeef ^ seed, h2 = 0x41c6ce57 ^ seed;
	for (let i = str.length - 1, ch; i >= 0; i--) {
		ch = str.charCodeAt(i);
		h1 = Math.imul(h1 ^ ch, 2654435761);
		h2 = Math.imul(h2 ^ ch, 1597334677);
	}
	h1  = Math.imul(h1 ^ (h1 >>> 16), 2246822507);
	h1 ^= Math.imul(h2 ^ (h2 >>> 13), 3266489909);
	h2  = Math.imul(h2 ^ (h2 >>> 16), 2246822507);
	h2 ^= Math.imul(h1 ^ (h1 >>> 13), 3266489909);

	return 4294967296 * (2097151 & h2) + (h1 >>> 0);
}


// Returns a "debounced" function version of a callback. callback params can be used in debounced version as well.
// delay can be number of milliseconds to debounce or a function that returns that number on every cb.
// opts can have:
//      leading: if truthy, cb called immediately if not called recently within delay.
//      ifInit: if truthy, cb called immediately if first time called.
// debounced.cancel(): cancel any currently pending trigger.
// debounced.triggerIfDebounced(): issue cb if currently on debouncing.
// Warning: not React aware - care should be taken to cancel if component removed.
//

/**
 * Create a debounced version of a callback. Callback args can be used in debounced version as well.
 * Warning: not React aware - care should be taken to cancel if component removed.
 *
 * @typeParam T - Callback signature
 * @param cb      The callback to debounce.
 * @param delay   Milliseconds to wait || a function that returns the delay when invoking debounce.
 * @param opts    Configuration options. See {@link DebounceOptions}.
 *
 * @returns A debounced function with {@link Debounced} methods.
 */
export function debounce<T extends(...args: any[]) => any>(
		cb: T,
		delay: number | (() => number) = 0,
		{leading, ifInit}: DebounceOptions = {}
) {
	let prevAt, timer;

	const debounced = ((...args) => {
		const msDelay = funcOrValOr(delay);
		const now = Date.now();
		const isRecent = now - prevAt < msDelay, isInit = !prevAt;
		prevAt = now;
		debounced.trigger = () => (debounced.cancel(), cb(...args));

		debounced.cancel();
		if (!msDelay || (!isRecent && leading) || (isInit && ifInit))
			debounced.trigger();
		else
			timer = setTimeout(debounced.trigger, msDelay);
	}) as Debounced<T>;
	debounced.cancel = () => (clearTimeout(timer), timer = undefined);
	debounced.triggerIfDebounced = () => timer && debounced.trigger();

	return debounced;
}

// Debounced wrapper around the original callback.
export type Debounced<T extends (...args: any[]) => any> = {
	(...args): void;
    // Cancel any currently pending trigger.
	cancel(): void;
    // Immediately invoke the callback (after cancelling any timeout).
	trigger(): ReturnType<T>;
    // Invoke the callback **only** if a timeout is currently pending.
	triggerIfDebounced(): ReturnType<T> | undefined;
};

export type DebounceOptions = {
    // Call the callback immediately if not called recently (within delay).
	leading?: boolean;
    // Call the callback immediately on the first invocation.
	ifInit?: boolean;
};


// Returns a debounced version of func that delays invoking by milliseconds. (based on
// delay can be a dynamic callback function issued when actually calling the func.
//
// options:
//   if leading: call func immediately if not called recently within delay.
//   if accumulate: debounced func will be called with an array of all the arguments received while waiting.
//
export function debounceEx<T extends(...args: any[]) => any>(
		cb: T, delay: number | (() => number) = 0, opts: {leading?: boolean; accumulate?: boolean} = {}
): (...args: any[]) => Promise<ReturnType<T>> {
	let lastCallAt: number | undefined;
	const deferred: {promise?: Promise<any>; resolve?: (value: any) => void; reject?: (reason?: any) => void} = {};
	let timer: ReturnType<typeof setTimeout> | undefined;
	let pendingArgs: any[][] = [];

	function debounced(this: any, ...args: any[]): any {
		const msDelay = typeof delay === 'function' ? delay() : delay; // Allow delay to be a function
		const now = Date.now();
		const isRecent = lastCallAt !== undefined && now - lastCallAt < msDelay;
		lastCallAt = now;

		pendingArgs.push(args);

		if (!timer)
			deferred.promise = new Promise((resolve, reject) => Object.assign(deferred, {resolve, reject}));
		else
			clearTimeout(timer);

		if (!isRecent && opts.leading)
			return flush();

		timer = setTimeout(flush, msDelay);

		if (opts.accumulate) {
			const argsIndex = pendingArgs.length - 1;
			return deferred.promise!.then(results => results[argsIndex]);
		}

		return deferred.promise!;
	}

	function flush(this: any) {
		const curDeferred = {...deferred};
		const curArgs = pendingArgs;
		timer = undefined;
		pendingArgs = [];

		return Promise.resolve(
			opts.accumulate ? cb.call(this, curArgs) : cb.apply(this, curArgs[curArgs.length - 1])
		).then(curDeferred.resolve!, curDeferred.reject!);
	}

	return debounced;
}


interface DataCached {
	jKey?: string;
	json?: string;
	created?: Date;
	accessed?: Date;
}

let cacheCurSize = 0;
let cacheDict: DataCached = {};

/**
 * Stores objects in memory cache. If exceeds maxCacheSize, purges least accessed objects. Objects are stored as json strings.
 * @param key - identifies the data object. Can be a string or an object (which will be stringified).
 * @param data - actual data object to store
 * @param maxCacheSize - (default 16Mb)
 * @param curObjectAlwaysKept - allow current object to exceed cache size (default true)
 */
export function cacheData({
	key, data, maxCacheSize = 1024 ** 2 * 16, curObjectAlwaysKept = true, created
}) {
	const jKey = typeof key === 'string'? key : JSON.stringify(key);    // stringify key if needed
    //console.log("cacheData jKey", jKey)
	if (cacheDict[jKey]) {
		cacheCurSize -= cacheDict[jKey].json.length;                    // remove previous object size
		delete cacheDict[jKey];
	}
	const json = JSON.stringify(data);
	const size = json.length;
	if (!curObjectAlwaysKept && size > maxCacheSize) return;

	cacheCurSize += size;
	if (cacheCurSize > maxCacheSize)
		Object.values(cacheDict)
			.sort((a, b) => a.accessed - b.accessed)
			.find(obj => {
				cacheCurSize -= obj.json.length;
				delete cacheDict[obj.jKey];
				return cacheCurSize <= maxCacheSize;
			});
	cacheDict[jKey] = {jKey, json, created: created ?? new Date(), accessed: new Date()};
    //console.log(`session cached jKey: ${jKey}. cacheCurSize: ${abvNum(cacheCurSize)}b`)
}

/**
 * Fetch object from memory cache, based on key.
 * @param key
 * @return {{data: any, created: date}|null}
 * data - cached object.
 * age - in milliseconds since created.
 */
export function cacheFetch(key) {
	const jKey = typeof key === 'string'? key : JSON.stringify(key);    // stringify key if needed
	const obj = cacheDict[jKey];
	if (!obj) return null;
	obj.accessed = new Date();
	return {data: JSON.parse(obj.json), created: obj.created};
}

export function cacheDirty({key}) {
	const jKey = typeof key === 'string'? key : JSON.stringify(key);    // stringify key if needed
	if (!cacheDict[jKey]) return;
	cacheCurSize = Math.max(cacheCurSize - cacheDict[jKey].json.length, 0);
	delete cacheDict[jKey];
}

export function cacheClear() {
	cacheDict = {};
	cacheCurSize = 0;
}

export function keepFirstItem(obj) {
	const firstKey = Object.keys(obj)[0];

	return firstKey !== undefined
		? {[firstKey]: obj[firstKey]}
		: {};
}
